import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Card, Grid, Stack, Typography, Chip, Button, Divider, alpha, useTheme, TextField, InputAdornment, IconButton, Dialog, DialogContent, DialogTitle, Tooltip, Snackbar } from '@mui/material';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { OrderStep } from 'src/@types/order/order.type';

interface Props { 
  steps: OrderStep[]; 
  order?: any;
}

const getScoreColor = (s: number | undefined) => {
  if (s === undefined || s === null) return '#94A3B8';
  if (s >= 0.8) return '#16A34A';
  if (s >= 0.5) return '#D97706';
  return '#E11D48';
};
const getScoreBg = (s: number | undefined) => {
  if (s === undefined || s === null) return 'transparent';
  if (s >= 0.8) return '#F0FDF4';
  if (s >= 0.5) return '#FFFBEB';
  return '#FFF1F2';
};

const syntaxHL = (t: string) => t
  .replace(/"([^"]+)":/g, '<span style="color:#6F42C1">"$1"</span>:')
  .replace(/: "([^"]+)"/g, ': <span style="color:#032F62">"$1"</span>')
  .replace(/: (true|false)/g, ': <span style="color:#005CC5">$1</span>')
  .replace(/: (\d+\.?\d*)/g, ': <span style="color:#005CC5">$1</span>');

const cleanTranscript = (t: string | undefined) => {
  if (!t) return '(silence)';
  const lower = t.toLowerCase().trim();
  if (lower === 'confirm_order') return 'confirmer la commande';
  if (lower === 'cancel_order') return 'annuler la commande';
  return t;
};

export default function AiTranscriptAnalysis({ steps, order }: Props) {
  const theme = useTheme();
  const [activeBlock, setActiveBlock] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [filterSpeaker, setFilterSpeaker] = useState<'ALL'|'CUSTOMER'|'AI'>('ALL');
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const scrollRef = useRef<any>(null);
  const hasAnomalies = steps.some(s => s.wasCorrected || s.isRepeat || (s.score && s.score < 0.8));
  

  // Stats
  const stats = useMemo(() => {
    const customerSteps = steps.filter(s => s.speakerRole === 'CUSTOMER');
    const scores = customerSteps.map(s => s.score).filter((s): s is number => s !== undefined);
    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    const corrections = customerSteps.filter(s => s.wasCorrected).length;
    const anomalyCount = customerSteps.filter(s => s.score !== undefined && s.score < 0.5).length;
    
    let minT = Infinity;
    let maxT = -Infinity;
    steps.forEach(s => {
      if (s.createdAt) {
        const t = new Date(s.createdAt as string).getTime();
        if (t < minT) minT = t;
        if (t > maxT) maxT = t;
      }
    });

    const durationMs = (minT !== Infinity && maxT !== -Infinity) ? maxT - minT : 0;
    const durationStr = durationMs > 0 ? `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s` : '0m 0s';
    return { avgScore, corrections, anomalyCount, durationStr, total: customerSteps.length };
  }, [steps]);

  const getAiResponse = (step: OrderStep) => {
    const i = step.intentDetected;
    if (i === 'CONFIRM_ORDER' || i === 'CONFIRM') return "Merci ! Votre commande est validée. Veuillez passer au guichet suivant.";
    if (i === 'REMOVE_ITEM' || i === 'REMOVE') return "Article retiré.";
    if (i === 'ORDER_ITEM' || i === 'ADD') return step.wasCorrected ? "Quel article exactement ?" : "C'est noté.";
    if (i === 'REPLACE') return "J'ai effectué le remplacement.";
    if (i === 'CANCEL') return "La commande a été annulée.";
    if (i === 'GREETING') return "Bonjour, je vous écoute.";
    if (step.stepType === 'ABANDONMENT') return "Commande annulée.";
    if (step.stepType === 'TRIO_SELECTION') return "Trio ajouté.";
    if (step.wasCorrected || step.stepType === 'ESCALATION') return "Erreur, veuillez répéter.";
    return "D'accord, c'est noté.";
  };

  // 2. Construction complète des étapes (Client + AILYN)
  const displaySteps = useMemo(() => {
    const res: OrderStep[] = [];
    const hasAi = steps.some(s => s.speakerRole === 'AI');
    const hasStart = steps.some(s => s.intentDetected === 'GREETING' || s.transcript?.toLowerCase().includes('commencer'));
    const hasEnd = steps.some(s => s.intentDetected === 'CONFIRM_ORDER' || s.stepType === 'CONFIRMATION' || s.transcript?.toLowerCase().includes('valider'));
    const t0 = steps.length > 0 ? steps[0].createdAt : undefined;

    // 1. Déterminer si c'est une commande vocale IA (Gemini)
    const isAiVoiceOrder = (order as any)?.orderSource === 'AI_VOICE' || hasAi;

    // Pour les commandes IA, on ne veut SURTOUT PAS injecter de fausses réponses
    if (isAiVoiceOrder) {
      return [...steps].sort((a, b) => (a.stepIndex || 0) - (b.stepIndex || 0));
    }

    // --- LOGIQUE LEGACY (Pour les commandes manuelles sans historique IA complet) ---
    if (!hasStart && steps.length > 0) {
      const d = t0 ? new Date(t0 as string) : new Date();
      if (t0) d.setSeconds(d.getSeconds() - 10);
      res.push({ speakerRole: 'CUSTOMER', transcript: 'commencer la commande', intentDetected: 'GREETING', orderJson: '{"status":"STARTED"}', createdAt: d.toISOString() } as OrderStep);
      const d2 = new Date(d); d2.setSeconds(d2.getSeconds() + 1);
      res.push({ speakerRole: 'AI', transcript: 'Bonjour, je vous écoute.', intentDetected: 'GREETING', orderJson: '{"status":"STARTED"}', createdAt: d2.toISOString() } as OrderStep);
    }

    steps.forEach(step => {
      res.push(step);
      // Injection de réponse pour la version legacy (pas d'IA dans les steps)
      if (!hasAi && step.speakerRole === 'CUSTOMER') {
        let ac = step.createdAt;
        if (step.createdAt) { const dd = new Date(step.createdAt); dd.setSeconds(dd.getSeconds() + 1); ac = dd.toISOString(); }
        res.push({ ...step, speakerRole: 'AI' as const, transcript: getAiResponse(step), score: 0.99, wasCorrected: false, isRepeat: false, createdAt: ac, isInjected: true } as any);
      }
    });

    if (!hasEnd && res.length > 0) {
      const lc = res[res.length - 1].createdAt;
      const ld = lc ? new Date(lc as string) : new Date();
      if (lc) ld.setSeconds(ld.getSeconds() + 10);
      res.push({ speakerRole: 'CUSTOMER', transcript: 'valider la commande', intentDetected: 'CONFIRM_ORDER', orderJson: '{"status":"CONFIRMED"}', createdAt: ld.toISOString() } as OrderStep);
      const ed = new Date(ld); ed.setSeconds(ed.getSeconds() + 1);
      res.push({ speakerRole: 'AI', transcript: 'Merci ! Votre commande est validée. Veuillez passer au guichet suivant.', intentDetected: 'CONFIRM_ORDER', orderJson: '{"status":"CONFIRMED"}', createdAt: ed.toISOString() } as OrderStep);
    }

    return res;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [steps, displaySteps]);

  // Build latency map
  const latencyMap = useMemo(() => {
    const map: Record<number, number> = {};
    displaySteps.forEach((step, idx) => {
      if (step.speakerRole === 'AI' && idx > 0) {
        const prev = displaySteps[idx - 1];
        if (prev.speakerRole === 'CUSTOMER' && step.createdAt && prev.createdAt) {
          const diff = new Date(step.createdAt as string).getTime() - new Date(prev.createdAt as string).getTime();
          map[idx] = diff;
        }
      }
    });
    return map;
  }, [displaySteps]);

  // Build consolidated JSON lines with colors + step-to-line mapping
  const { jsonLines, stepLineMap } = useMemo(() => {
    const lines: { text: string; bg: string }[] = [];
    const lineMap: Record<number, number> = {}; // displayStep index -> first JSON line
    const items: { data: any; bg: string; stepIdx: number }[] = [];
    const anomalies: any[] = [];
    let scoreSum = 0;
    let scoreCount = 0;

    displaySteps.forEach((step, stepIdx) => {
      if (step.speakerRole !== 'CUSTOMER' || !step.orderJson) return;
      try {
        const p = JSON.parse(step.orderJson);
        if (p.items) p.items.forEach((it: any) => {
          if (it.productName === 'unknown' && !it.name) return;
          items.push({ data: it, bg: getScoreBg(step.score), stepIdx });
        });
        if (p.anomalies) {
          p.anomalies.forEach((a: any) => { anomalies.push(a); });
        }
        // If score is low but no explicit anomaly, add one
        if (step.score != null && step.score < 0.5 && (!p.anomalies || p.anomalies.length === 0)) {
          anomalies.push({ type: 'LOW_CONFIDENCE', message: `Confiance IA faible (${(step.score * 100).toFixed(0)}%)`, transcript: step.transcript });
        }
        if (step.score != null) { scoreSum += step.score; scoreCount += 1; }
      } catch (e) { /* ignore */ }
    });

    const avg = scoreCount > 0 ? +(scoreSum / scoreCount).toFixed(2) : 0;
    lines.push({ text: '{', bg: 'transparent' });
    lines.push({ text: '  "items": [', bg: 'transparent' });

    items.forEach((ci, idx) => {
      const firstLine = lines.length;
      if (lineMap[ci.stepIdx] === undefined) lineMap[ci.stepIdx] = firstLine;
      const s = JSON.stringify(ci.data, null, 2).split('\n');
      s.forEach((line, i) => {
        let suffix = '';
        if (i === s.length - 1 && idx < items.length - 1) suffix = ',';
        lines.push({ text: `    ${line}${suffix}`, bg: ci.bg });
      });
    });

    lines.push({ text: '  ],', bg: 'transparent' });
    lines.push({ text: '  "notes": [],', bg: 'transparent' });
    lines.push({ text: `  "confidence_score": ${avg},`, bg: 'transparent' });

    const anomBg = anomalies.length > 0 ? '#FEEBC8' : 'transparent';
    lines.push({ text: '  "anomalies": [', bg: anomBg });
    anomalies.forEach((a, idx) => {
      const s = JSON.stringify(a, null, 2).split('\n');
      s.forEach((line, i) => {
        let suffix = '';
        if (i === s.length - 1 && idx < anomalies.length - 1) suffix = ',';
        lines.push({ text: `    ${line}${suffix}`, bg: anomBg });
      });
    });
    lines.push({ text: '  ]', bg: anomBg });
    lines.push({ text: '}', bg: 'transparent' });
    return { jsonLines: lines, stepLineMap: lineMap };
  }, [displaySteps]);

  // Highlight text based on intent and scores
  const highlight = (step: OrderStep): string => {
    const t = cleanTranscript(step.transcript);
    let r = t;

    // 1. Highlight Products from the order
    if (order?.orderedProducts && r) {
      order.orderedProducts.forEach((op: any) => {
        const pName = op.product?.name;
        if (pName) {
          const regex = new RegExp(`(${pName})`, 'gi');
          r = r.replace(regex, `<span style="color:${theme.palette.primary.main}; font-weight:700; background-color:${alpha(theme.palette.primary.main, 0.08)}; border-radius:4px; padding:0 3px; border:1px solid ${alpha(theme.palette.primary.main, 0.2)}">$1</span>`);
        }
      });
    }

    // 2. Coloration logic for Upsell Engine

    // Coloration logic
    let bgColor = 'transparent';
    let textColor = 'inherit';
    let label = '';

    if (step.stepType === 'UPSELL_SUGGESTION') {
        bgColor = alpha(theme.palette.warning.main, 0.1);
        textColor = theme.palette.warning.dark;
        label = 'Suggestion';
    } else if (step.stepType === 'UPSELL_ACCEPTED') {
        bgColor = alpha(theme.palette.success.main, 0.1);
        textColor = theme.palette.success.dark;
        label = 'Accepté';
    } else if (step.stepType === 'UPSELL_DECLINED') {
        bgColor = alpha(theme.palette.error.main, 0.1);
        textColor = theme.palette.error.dark;
        label = 'Refusé';
    }

    // If global score is low (anomaly), highlight the whole transcript
    if (step.speakerRole === 'CUSTOMER' && step.score != null && step.score < 0.5) {
      const c = 'background:#FFE4E6;color:#BE123C';
      const tooltip = `Confiance Faible: ${(step.score * 100).toFixed(0)}%`;
      r = `<span style="${c};padding:2px 6px;border-radius:4px;font-weight:600" title="${tooltip}">${t}</span>`;
    }

    try {
      if (step.orderJson) {
        const d = JSON.parse(step.orderJson);
        const words: { w: string; s: number; type: string }[] = [];
        
        d.items?.forEach((it: any) => {
          const name = it.productName || it.name;
          if (name && name !== 'unknown') {
              words.push({ 
                  w: name, 
                  s: step.productScore ?? step.score ?? 1,
                  type: step.intentDetected === 'REMOVE' || step.intentDetected === 'REMOVE_ITEM' ? 'REMOVE' : 'ADD'
              });
          }
        });

        // Add suggested products from upsell rule if present in JSON or if it's an upsell step
        if (step.stepType === 'UPSELL_SUGGESTION') {
            // Find the product name in the transcript
            // This is a bit heuristic
        }

        const seen = new Set<string>();
        words.filter(x => x.w && x.w.length >= 3).sort((a, b) => b.w.length - a.w.length).forEach(({ w, s, type }) => {
          if (seen.has(w.toLowerCase())) return;
          seen.add(w.toLowerCase());
          
          let c = 'background:#DCFCE7;color:#166534'; // Default ADD (Green)
          if (type === 'REMOVE') {
              c = 'background:#FEE2E2;color:#991B1B'; // REMOVE (Red)
          } else if (step.stepType === 'UPSELL_SUGGESTION') {
              c = 'background:#FEF3C7;color:#92400E'; // SUGGESTION (Yellow/Amber)
          }

          const e = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          r = r.replace(new RegExp(`(${e})`, 'gi'), `<span style="${c};padding:2px 4px;border-radius:4px;font-weight:600">$1</span>`);
        });
      }
    } catch (e) { /* ignore */ }

    if (label) {
        return `<span style="font-size:10px;text-transform:uppercase;font-weight:900;margin-right:8px;padding:2px 4px;border-radius:3px;background:${textColor};color:#fff">${label}</span>${r}`;
    }

    return r;
  };

  const onClickMsg = (stepIndex: number) => {
    // Find the JSON line that corresponds to this step
    const targetLine = stepLineMap[stepIndex];
    if (targetLine !== undefined) {
      const el = document.getElementById(`json-line-${targetLine}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setActiveBlock(targetLine);
        setTimeout(() => setActiveBlock(null), 2000);
      }
    }
  };

  const handleExport = () => {
    const exp = {
      timestamp: new Date().toISOString(),
      totalSteps: displaySteps.length,
      conversation: displaySteps.map(s => {
        let j = null; try { if (s.orderJson) j = JSON.parse(s.orderJson); } catch (e) { /* */ }
        return { speaker: s.speakerRole === 'CUSTOMER' ? 'Client' : 'AILYN', time: s.createdAt ? new Date(s.createdAt).toLocaleTimeString('fr-FR') : 'N/A', transcript: s.transcript, intent: s.intentDetected, scores: s.speakerRole === 'CUSTOMER' ? { overall: s.score, product: s.productScore, quantity: s.quantityScore, modifier: s.modifierScore } : undefined, json: j };
      }),
    };
    const a = document.createElement('a');
    a.href = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(exp, null, 2))}`;
    a.download = `analyse_ailyn_${Date.now()}.json`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleCopyJson = () => {
    const text = jsonLines.map(l => l.text).join('\n');
    navigator.clipboard.writeText(text).then(() => { setCopied(true); });
  };

  // Filtered steps for search/filter — on conserve l'index d'origine dans displaySteps
  // pour que le clic (scroll JSON) et la latence pointent toujours sur la bonne donnée.
  const filteredSteps = useMemo(() => displaySteps
    .map((step, originalIndex) => ({ step, originalIndex }))
    .filter(({ step }) => {
      if (filterSpeaker !== 'ALL' && step.speakerRole !== filterSpeaker) return false;
      if (searchQuery && !step.transcript?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    }), [displaySteps, searchQuery, filterSpeaker]);

  let filterTooltip = 'Tous';
  if (filterSpeaker === 'CUSTOMER') filterTooltip = 'Client';
  else if (filterSpeaker === 'AI') filterTooltip = 'AILYN';

  let filterIcon = 'mdi:filter-variant';
  if (filterSpeaker === 'CUSTOMER') filterIcon = 'mdi:account';
  else if (filterSpeaker === 'AI') filterIcon = 'mdi:robot';

  return (
    <>
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Analyse de la compréhension IA</Typography>
            {hasAnomalies && <Chip label="Anomalies détectées" color="error" icon={<Iconify icon="mdi:alert-triangle" />} sx={{ bgcolor: alpha(theme.palette.error.main, 0.16), color: theme.palette.error.dark, fontWeight: 'bold' }} />}
          </Stack>
          <Button variant="outlined" startIcon={<Iconify icon="mdi:download" />} onClick={handleExport}>Exporter</Button>
        </Stack>
      </Box>

      {/* Stats Banner */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" spacing={4} justifyContent="center">
          <Stack alignItems="center"><Typography variant="h6" color="primary.main">{stats.durationStr}</Typography><Typography variant="caption" color="text.secondary">Durée</Typography></Stack>
          <Stack alignItems="center"><Typography variant="h6" sx={{ color: getScoreColor(stats.avgScore) }}>{(stats.avgScore * 100).toFixed(0)}%</Typography><Typography variant="caption" color="text.secondary">Score moyen</Typography></Stack>
          <Stack alignItems="center"><Typography variant="h6" color="text.disabled">N/A</Typography><Typography variant="caption" color="text.secondary">Corrections</Typography></Stack>
          <Stack alignItems="center"><Typography variant="h6" color="error.main">{stats.anomalyCount}</Typography><Typography variant="caption" color="text.secondary">Anomalies</Typography></Stack>
          <Stack alignItems="center"><Typography variant="h6">{stats.total}</Typography><Typography variant="caption" color="text.secondary">Messages</Typography></Stack>
        </Stack>
        
        {/* Timeline Visual */}
        <Box sx={{ mt: 2, height: 4, bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
          {displaySteps.filter(s => s.speakerRole === 'CUSTOMER').map((s, i, arr) => (
            <Box 
              key={i} 
              sx={{ 
                position: 'absolute', 
                left: `${(i / (arr.length - 1 || 1)) * 100}%`, 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: getScoreColor(s.score), 
                top: -2,
                transform: 'translateX(-50%)',
                boxShadow: theme.customShadows?.z1
              }} 
            />
          ))}
        </Box>
      </Box>

      <Grid container>
        {/* LEFT: Transcript */}
        <Grid item xs={12} md={5} sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Transcript de la conversation</Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => setShowSearch(!showSearch)}><Iconify icon="mdi:magnify" width={18} /></IconButton>
                <IconButton size="small" onClick={() => {
                  if (filterSpeaker === 'ALL') setFilterSpeaker('CUSTOMER');
                  else if (filterSpeaker === 'CUSTOMER') setFilterSpeaker('AI');
                  else setFilterSpeaker('ALL');
                }}>
                  <Tooltip title={filterTooltip}>
                    <Box><Iconify icon={filterIcon} width={18} /></Box>
                  </Tooltip>
                </IconButton>
              </Stack>
            </Stack>
            {showSearch && (
              <TextField size="small" fullWidth placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ mt: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="mdi:magnify" width={16} /></InputAdornment> }}
              />
            )}
          </Box>
          <Divider />
          <Box ref={scrollRef} sx={{ height: 600, p: 2, overflowY: 'auto' }}>
            <Stack spacing={1.5}>
              {filteredSteps.map(({ step, originalIndex: index }) => {
                const isUser = step.speakerRole === 'CUSTOMER';
                const time = step.createdAt ? new Date(step.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--';
                return (
                  <Box key={index}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      {isUser ? (
                        <Iconify icon="mdi:account" sx={{ color: theme.palette.primary.main, width: 22, height: 22 }} />
                      ) : (
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Iconify icon="mdi:waveform" sx={{ color: '#fff', width: 14, height: 14 }} />
                        </Box>
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{isUser ? 'Client' : 'AILYN'}</Typography>
                      <Typography variant="caption" color="text.secondary">{time}</Typography>
                      {!isUser && latencyMap[index] !== undefined && !(displaySteps[index] as any).isInjected && (
                        (() => {
                          const lat = latencyMap[index];
                          let color = theme.palette.success.main;
                          let bg = alpha(theme.palette.success.main, 0.1);
                          if (lat >= 3000) {
                            color = theme.palette.error.main;
                            bg = alpha(theme.palette.error.main, 0.1);
                          } else if (lat >= 1000) {
                            color = theme.palette.warning.main;
                            bg = alpha(theme.palette.warning.main, 0.1);
                          }
                          return (
                            <Chip 
                              size="small" 
                              label={`${(lat / 1000).toFixed(1)}s`} 
                              sx={{ 
                                height: 18, 
                                fontSize: 10, 
                                bgcolor: bg,
                                color,
                                border: 'none'
                              }} 
                            />
                          );
                        })()
                      )}
                    </Stack>
                    <Box
                      onClick={() => onClickMsg(index)}
                      sx={{
                        p: 1.5, px: 2, ml: isUser ? 3 : 4,
                        borderRadius: 2, bgcolor: '#F4F6F8', maxWidth: '88%', display: 'inline-block',
                        cursor: 'pointer', transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px rgba(0,0,0,0.06)', bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      <Typography variant="body2">
                        <Box component="span" dangerouslySetInnerHTML={{ __html: highlight(step) }} />
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {filteredSteps.length === 0 && <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 5 }}>Aucun résultat.</Typography>}
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16A34A' }} />
                <Typography variant="caption" color="text.secondary">Haute (&gt;80%)</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#D97706' }} />
                <Typography variant="caption" color="text.secondary">Moyenne</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#E11D48' }} />
                <Typography variant="caption" color="text.secondary">Faible</Typography>
              </Stack>
            </Stack>
          </Box>
        </Grid>

        {/* RIGHT: Consolidated JSON */}
        <Grid item xs={12} md={7}>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">JSON interprété par AILYN</Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Copier"><IconButton size="small" onClick={handleCopyJson}><Iconify icon="mdi:content-copy" width={18} /></IconButton></Tooltip>
                <Tooltip title="Plein écran"><IconButton size="small" onClick={() => setFullscreen(true)}><Iconify icon="mdi:fullscreen" width={18} /></IconButton></Tooltip>
              </Stack>
            </Stack>
          </Box>
          <Divider />
          <Scrollbar sx={{ height: 647, bgcolor: '#fff' }}>
            <Box sx={{ fontFamily: '"Fira Code", "Consolas", monospace', fontSize: 13, lineHeight: 1.7, color: '#333' }}>
              {jsonLines.map((line, i) => {
                let hoverBg = line.bg;
                if (activeBlock === i) {
                  hoverBg = alpha(theme.palette.primary.main, 0.2);
                } else if (line.bg === 'transparent') {
                  hoverBg = alpha(theme.palette.grey[500], 0.04);
                }
                return (
                <Box
                  key={i}
                  id={`json-line-${i}`}
                  sx={{
                    display: 'flex',
                    bgcolor: activeBlock === i ? alpha(theme.palette.primary.main, 0.2) : line.bg,
                    transition: 'background-color 0.3s',
                    '&:hover': { bgcolor: hoverBg },
                  }}
                >
                  <Box sx={{ width: 40, minWidth: 40, textAlign: 'right', pr: 2, color: '#B0B0B0', userSelect: 'none', borderRight: '1px solid #eee' }}>
                    {i + 1}
                  </Box>
                  <Box sx={{ pl: 2, whiteSpace: 'pre' }} dangerouslySetInnerHTML={{ __html: syntaxHL(line.text) }} />
                </Box>
              ); })}
            </Box>
          </Scrollbar>
        </Grid>
      </Grid>
    </Card>

    {/* Fullscreen JSON Dialog */}
    <Dialog open={fullscreen} onClose={() => setFullscreen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        JSON interprété par AILYN
        <IconButton onClick={() => setFullscreen(false)}><Iconify icon="mdi:close" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre', bgcolor: '#f9f9f9', p: 2, borderRadius: 1, maxHeight: '70vh', overflow: 'auto' }}>
          {jsonLines.map((l, i) => <Box key={i} sx={{ bgcolor: l.bg, px: 1 }}><span style={{ color: '#B0B0B0', marginRight: 12 }}>{i + 1}</span><span dangerouslySetInnerHTML={{ __html: syntaxHL(l.text) }} /></Box>)}
        </Box>
      </DialogContent>
    </Dialog>

    {/* Copy snackbar */}
    <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} message="JSON copié !" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}
